/**
 * useIsWlUser — Hook compartilhado que indica se o usuário autenticado
 * é um operador ativo do painel White Label (`wl_users`).
 *
 * Por que existe:
 *   Antes, três pontos diferentes (`Index.tsx`, `SelectContext.tsx`, headers)
 *   disparavam a RPC `is_wl_user` independentemente, gerando 3+ requests por
 *   navegação. Centralizando aqui, o TanStack Query reusa a mesma queryKey
 *   e mantém 1 request por usuário a cada 5 minutos.
 *
 * Segurança:
 *   - Sem cache local em localStorage (não é informação para persistir cliente).
 *   - Sem fallback otimista — `true` só após a RPC confirmar.
 *   - Os guards reais de rota (`WhiteLabelProtectedRoute`,
 *     `TenantProtectedRoute`) continuam fazendo sua própria validação;
 *     este hook é apenas UX (mostrar/esconder switcher e roteamento inicial).
 *
 * CHANGELOG:
 *   v1.0.0 — 2026-05-29 — Implementação inicial.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useIsWlUser');

export function useIsWlUser() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['is_wl_user', user?.id],
    queryFn: async (): Promise<boolean> => {
      if (!user?.id) return false;
      const { data, error } = await supabase.rpc('is_wl_user', { _user_id: user.id });
      if (error) {
        logger.warn('Falha em is_wl_user', error);
        // Não derruba a UI; devolve false (sem switcher aparente).
        return false;
      }
      return data === true;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    isWlUser: query.data === true,
    isLoading: query.isLoading,
    isResolved: query.isFetched, // útil para evitar flicker antes da resposta
  };
}
